import Miner from "@components/global/new/Miner/Miner";
import mainLocationsStore from "@mobx/store/locations";
import { ItemStore } from "@mobx/store/locations/interfaces";
import { DRAGGING_TYPES3, IRackItem3, ISomeMinerItem } from "@mobx/store/locations/types";
import { reorderUnnalocatedItems } from "@store/unallocatedMiners/actions";
import { useScrollByDragging } from "@utils/hooks/useScrollByDragging";
import cn from "classnames";
import { observer } from "mobx-react-lite";
import { DragEvent, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import TooltipMenu3 from "../TooltipMenu3";
import styles from "./UnrackItem.module.scss";

export interface IUnrackItem3Props {
  readonly itemStore: ItemStore;
}

type DragEventType3 = DragEvent<HTMLDivElement>;

const useControl = (
  props: IUnrackItem3Props
) => {
  const dragCounter = useRef(0);
  const dispatch = useDispatch();
  const [dragging, setDragging] = useState(false);
  const [dragEnter, setDragEnter] = useState(false);
  const wrapper = useRef<HTMLDivElement>(null);
  const item = props.itemStore.item as ISomeMinerItem;
  const locationStore = mainLocationsStore.activeLocation;

  useScrollByDragging(dragging, wrapper.current);

  const cancelAll = () => {
    setDragging(false);
    setDragEnter(false);
    dragCounter.current = 0;
  };

  const onDragStart = (e: DragEventType3) => {
    const stringData = JSON.stringify({
      ...item,
    });
    setDragging(true);
    e.dataTransfer.setData(DRAGGING_TYPES3.UNRACK_ITEM, stringData);
    e.dataTransfer.dropEffect = "move";
    e.dataTransfer.effectAllowed = "move";
    window.REORDER_UNRACK_ITEM = { id: item.id };
  };

  const onDragEnd = (e: DragEventType3) => {
    cancelAll();
  };

  const onDrop = (e: DragEventType3) => {
    if (window.REORDER_UNRACK_ITEM) {
      if (window.REORDER_UNRACK_ITEM.id === item.id) return;

      dispatch(
        reorderUnnalocatedItems({
          draggedId: window.REORDER_UNRACK_ITEM.id,
          placedId: item.id,
        })
      );
    }

    cancelAll();
  };

  const onDragEnter = (e: DragEventType3) => {
    if (dragging) return;
    dragCounter.current++;
    setDragEnter(true);
  };
  const onDragLeave = (e: DragEventType3) => {
    dragCounter.current--;
    if (dragCounter.current <= 0) {
      setDragEnter(false);
      dragCounter.current = 0;
    }
  };

  const filtered = useMemo(() => {
    if (!locationStore?.selectedUsers?.size) return;
    return locationStore?.selectedUsers.has((item as IRackItem3).user_id);
  }, [locationStore?.selectedUsers]);

  return {
    dragging,
    dragEnter,
    filtered,
    wrapper,
    item,

    onDragStart,
    onDragEnd,
    onDragEnter,
    onDrop,
    onDragLeave,
  };
};

const UnrackItem3 = observer((props: IUnrackItem3Props) => {
  const ctrl = useControl(props);

  return (
    <TooltipMenu3
      minerName={ctrl.item.miner_name}
      userId={ctrl.item.user_id}
      minerId={ctrl.item.id}
      itemStore={props.itemStore}
    >
      <div
        ref={ctrl.wrapper}
        className={cn(styles.wrapper, {
          [styles.dragging]: ctrl.dragging,
          [styles.dragEnter]: ctrl.dragEnter,
          [styles.notFiltered]:
            ctrl.filtered !== undefined && !ctrl.filtered,
        })}
        onDragStart={ctrl.onDragStart}
        onDragEnd={ctrl.onDragEnd}
        onDragOver={(e) => e.preventDefault()}
        onDragEnter={ctrl.onDragEnter}
        onDragLeave={ctrl.onDragLeave}
        onDrop={ctrl.onDrop}
        draggable
      >
        <Miner
          miner_id={ctrl.item.id}
          miner_name={ctrl.item.miner_name || ctrl.item.id.toString()}
          rejected={ctrl.item.rejected}
          accepted={ctrl.item.accepted}
          user_color={ctrl.item.user_color}
          user_id={""+ctrl.item.user_id}
          status={ctrl.item.status}
          useFullName
        />
      </div>
    </TooltipMenu3>
  );
});

UnrackItem3.displayName = "UnrackItem3";

export default UnrackItem3;